import React, { useState, useEffect } from 'react';
import { Car, Home, Anchor, Sofa, Package, Info, ChevronDown, Bed, Bath, Maximize, Calendar, Fuel, Settings, History, Sparkles, ParkingCircle, Shield, Wrench } from 'lucide-react';
import axios from '../lib/axios';
import { API, useLanguage } from '../App';

// Icon mapping for property groups
const groupIcons = {
  'Vehicle Details': Car,
  'History': History,
  'Property Details': Home,
  'Parking': ParkingCircle,
  'Amenities': Sparkles,
  'Boat Details': Anchor,
  'Item Details': Package,
  'Condition': Wrench,
  'Safety': Shield,
  'default': Info
};

// Get appropriate icon for a group
const getGroupIcon = (groupName) => {
  return groupIcons[groupName] || groupIcons['default'];
};

// Format property value for display
const formatPropertyValue = (value, property) => {
  if (value === null || value === undefined || value === '') return null;
  
  // Boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  // Number with unit
  if (property?.unit && typeof value === 'number') {
    return `${value.toLocaleString()} ${property.unit}`;
  }
  
  // Regular number - but don't format years (4-digit numbers between 1900-2100)
  if (typeof value === 'number') {
    if (Number.isInteger(value) && value >= 1900 && value <= 2100) {
      return String(value);
    }
    return value.toLocaleString();
  }
  
  // Array values
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  
  return String(value);
};

// Get property display name based on language
const getPropertyName = (property, language = 'en') => {
  if (!property?.name) return property?.key || 'Unknown';
  
  if (typeof property.name === 'string') return property.name;
  
  return property.name[language] || property.name['en'] || property.key || 'Unknown';
};

const ListingPropertyGroups = ({ listing, categoryProperties = [] }) => {
  const { language } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get all property values from custom_properties (single source of truth)
  const getAllPropertyValues = () => {
    const values = {};
    
    // custom_properties is the single source of truth
    if (listing.custom_properties) {
      Object.entries(listing.custom_properties).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          values[key] = value;
        }
      });
    }
    
    return values;
  };

  const propertyValues = getAllPropertyValues();
  
  // Group properties by their group field
  const groupedProperties = () => {
    const groups = {};
    
    // If we have category properties with group info, use them
    if (categoryProperties.length > 0) {
      categoryProperties.forEach(prop => {
        const groupName = prop.group || 'Details';
        const value = propertyValues[prop.key];
        
        // Only include properties that have values
        if (value !== null && value !== undefined && value !== '') {
          if (!groups[groupName]) {
            groups[groupName] = [];
          }
          groups[groupName].push({
            ...prop,
            value: value
          });
        }
      });
    } else {
      // Fallback: Create groups based on existing data
      // Vehicle Details Group
      const vehicleProps = ['brand', 'model', 'year', 'color', 'mileage', 'fuel_type', 'transmission'];
      const vehicleData = vehicleProps
        .filter(key => propertyValues[key])
        .map(key => ({ key, name: { en: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }, value: propertyValues[key] }));
      if (vehicleData.length > 0) groups['Vehicle Details'] = vehicleData;
      
      // History Group
      const historyProps = ['number_of_owners', 'service_history', 'damage_history', 'vin'];
      const historyData = historyProps
        .filter(key => propertyValues[key])
        .map(key => ({ key, name: { en: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }, value: propertyValues[key] }));
      if (historyData.length > 0) groups['History'] = historyData;
      
      // Property Details Group
      const propertyProps = ['property_type', 'num_rooms', 'num_bathrooms', 'living_area_m2', 'plot_size_m2', 'year_built', 'property_condition', 'energy_label'];
      const propertyData = propertyProps
        .filter(key => propertyValues[key])
        .map(key => ({ 
          key, 
          name: { en: key === 'num_rooms' ? 'Bedrooms' : key === 'num_bathrooms' ? 'Bathrooms' : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }, 
          value: propertyValues[key],
          unit: key.includes('_m2') ? 'm²' : null
        }));
      if (propertyData.length > 0) groups['Property Details'] = propertyData;
      
      // Parking Group
      const parkingProps = ['has_parking', 'parking_spaces', 'parking_fee'];
      const parkingData = parkingProps
        .filter(key => propertyValues[key])
        .map(key => ({ key, name: { en: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }, value: propertyValues[key] }));
      if (parkingData.length > 0) groups['Parking'] = parkingData;
      
      // Boat Details Group
      const boatProps = ['boat_type', 'boat_length', 'boat_year', 'engine_type', 'engine_hours', 'hull_material', 'registration_number'];
      const boatData = boatProps
        .filter(key => propertyValues[key])
        .map(key => ({ key, name: { en: key.replace(/boat_/g, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }, value: propertyValues[key] }));
      if (boatData.length > 0) groups['Boat Details'] = boatData;
      
      // Item Condition Group
      const itemProps = ['condition', 'material', 'age_years', 'dimensions', 'assembly_required'];
      const itemData = itemProps
        .filter(key => propertyValues[key])
        .map(key => ({ key, name: { en: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }, value: propertyValues[key] }));
      if (itemData.length > 0) groups['Condition'] = itemData;
    }
    
    return groups;
  };

  const groups = groupedProperties();
  const groupNames = Object.keys(groups);
  
  // Initialize activeTab with the first group (computed after groups are known)
  const [activeTab, setActiveTab] = useState(() => null);
  
  // Set initial active tab when groups are available
  useEffect(() => {
    if (groupNames.length > 0 && !activeTab) {
      setActiveTab(groupNames[0]);
    }
  }, [groupNames.length, activeTab]);

  // If no properties to show, return null
  if (groupNames.length === 0) {
    return null;
  }
  
  // Ensure activeTab is valid (fallback to first group if current tab doesn't exist)
  const currentTab = activeTab && groups[activeTab] ? activeTab : groupNames[0];

  // Render property value with special formatting
  const renderPropertyValue = (prop) => {
    const value = prop.value;
    
    // Energy label with color coding
    if (prop.key === 'energy_label' && value) {
      const labelColors = {
        'A': 'bg-green-500',
        'B': 'bg-lime-500',
        'C': 'bg-yellow-500',
        'D': 'bg-orange-400',
        'E': 'bg-orange-500',
        'F': 'bg-red-400',
        'G': 'bg-red-600'
      };
      return (
        <span className={`inline-block w-8 h-8 rounded-lg font-bold text-center leading-8 text-white ${labelColors[value] || 'bg-gray-500'}`}>
          {value}
        </span>
      );
    }
    
    // Property condition with badge
    if (prop.key === 'property_condition' && value) {
      return (
        <span className="capitalize">{value.replace(/_/g, ' ')}</span>
      );
    }
    
    // Property type badge
    if (prop.key === 'property_type' && value) {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value === 'private_property' 
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
        }`}>
          {value === 'private_property' ? 'Private Property' : 'Leasehold'}
        </span>
      );
    }
    
    return formatPropertyValue(value, prop);
  };

  // Render a group of properties
  const renderPropertyGroup = (groupName, properties) => {
    const GroupIcon = getGroupIcon(groupName);
    
    return (
      <div key={groupName} className="space-y-4">
        {/* Mobile: Show group header */}
        {isMobile && (
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 border-b border-border pb-2">
            <GroupIcon className="w-5 h-5 text-primary" />
            {groupName}
          </h3>
        )}
        
        {/* Properties grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {properties.map((prop, index) => {
            const displayValue = renderPropertyValue(prop);
            if (!displayValue) return null;
            
            // Check if this is a long text field (like VIN)
            const isLongText = prop.key === 'vin' || (typeof displayValue === 'string' && displayValue.length > 20);
            
            return (
              <div 
                key={prop.key || index}
                className={`p-3 bg-secondary/50 rounded-lg ${isLongText ? 'col-span-2 md:col-span-1' : ''}`}
              >
                <p className="text-xs text-muted-foreground mb-1">
                  {getPropertyName(prop, language)}
                </p>
                <p className={`font-medium text-foreground ${isLongText ? 'text-sm break-all' : ''}`}>
                  {displayValue}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Desktop: Tabbed interface
  if (!isMobile && groupNames.length > 1) {
    return (
      <div className="mt-6" data-testid="property-groups-tabs">
        {/* Tab Navigation */}
        <div className="flex border-b border-border overflow-x-auto scrollbar-hide">
          {groupNames.map((groupName) => {
            const GroupIcon = getGroupIcon(groupName);
            return (
              <button
                key={groupName}
                onClick={() => setActiveTab(groupName)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  currentTab === groupName
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${groupName.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <GroupIcon className="w-4 h-4" />
                {groupName}
              </button>
            );
          })}
        </div>
        
        {/* Tab Content */}
        <div className="py-4">
          {currentTab && groups[currentTab] && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {groups[currentTab].map((prop, index) => {
                const displayValue = renderPropertyValue(prop);
                if (!displayValue) return null;
                
                // Check if this is a long text field (like VIN)
                const isLongText = prop.key === 'vin' || (typeof displayValue === 'string' && displayValue.length > 20);
                
                return (
                  <div 
                    key={prop.key || index}
                    className={`p-3 bg-secondary/50 rounded-lg ${isLongText ? 'col-span-2 md:col-span-1' : ''}`}
                  >
                    <p className="text-xs text-muted-foreground mb-1">
                      {getPropertyName(prop, language)}
                    </p>
                    <p className={`font-medium text-foreground ${isLongText ? 'text-sm break-all' : ''}`}>
                      {displayValue}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Mobile or single group: Show all with headers
  return (
    <div className="mt-6 space-y-6" data-testid="property-groups-mobile">
      {groupNames.map((groupName) => renderPropertyGroup(groupName, groups[groupName]))}
    </div>
  );
};

export default ListingPropertyGroups;
